import {
  getLeoUuid,
  LeoUUID,
  getString,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
/* eslint-disable import/export */
export abstract class GenerateSurveyPreviewLinkRPC implements
  LeoRPC<GenerateSurveyPreviewLinkRPC.Request, GenerateSurveyPreviewLinkRPC.Response, GenerateSurveyPreviewLinkRPC.Errors.Errors>
{
  abstract execute(
    request: GenerateSurveyPreviewLinkRPC.Request
  ): Promise<LeoRPCResult<GenerateSurveyPreviewLinkRPC.Response, GenerateSurveyPreviewLinkRPC.Errors.Errors>>;
}

export namespace GenerateSurveyPreviewLinkRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    surveyId: string;
    projectId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGenerateSurveyPreviewLinkRPCRequest: boolean = true;
  
    readonly surveyId: LeoUUID;
    readonly projectId: LeoUUID;
  
  
    constructor(
    surveyId: LeoUUID, 
    projectId: LeoUUID
    ) {
      this.surveyId = surveyId;
      this.projectId = projectId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      return new Request(
        surveyId,
        projectId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          surveyId: this.surveyId.toDTO(),
          projectId: this.projectId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GenerateSurveyPreviewLinkRPC(surveyId='${this.surveyId}',projectId='${this.projectId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    previewURL: string;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGenerateSurveyPreviewLinkResponse: boolean = true;
  
    readonly previewURL: string;
  
  
    constructor(
    previewURL: string
    ) {
      Response.validatePreviewUrl(previewURL);
      this.previewURL = previewURL;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const previewURL = getString(dto, "previewURL", InvalidResponsePreviewUrlError);
      return new Response(
        previewURL
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          previewURL: this.previewURL
      };
      return dto;
    }
    private static validatePreviewUrl = (previewURL: string): void => {
      if(previewURL.trim().length === 0) {
        throw new InvalidResponsePreviewUrlNonEmptyError(
          `Attribute previewURL cannot be an empty string.`
        );
      }
    }
    
  }
  
  Response.prototype.toString = function toString(): string {
    return `GenerateSurveyPreviewLink(previewURL='${this.previewURL}')`;
  };
  

  export enum RPCError {
    InvalidSurveyId = "INVALID_SURVEY_ID",
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED"
  }

  export namespace Errors {
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `GenerateSurveyPreviewLinkRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GenerateSurveyPreviewLinkRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `GenerateSurveyPreviewLinkRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    

    export type Errors = InvalidSurveyId | InvalidProjectId | ProjectAlreadyArchived;
    export type ErrorsDTO = InvalidSurveyIdDTO | InvalidProjectIdDTO | ProjectAlreadyArchivedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSurveyIdError extends GenerateSurveyPreviewLinkRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestProjectIdError extends GenerateSurveyPreviewLinkRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidResponsePreviewUrlError extends GenerateSurveyPreviewLinkRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponsePreviewUrlError";
  }
}

export class InvalidResponsePreviewUrlNonEmptyError extends InvalidResponsePreviewUrlError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponsePreviewUrlNonEmptyError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends GenerateSurveyPreviewLinkRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GenerateSurveyPreviewLinkRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends GenerateSurveyPreviewLinkRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

